import React, { useEffect, useState } from "react";
import { Alert, Box, Chip, Snackbar, Typography } from "@mui/material";
import Table from "components/Table";
import { Link, useSearchParams } from "react-router-dom";
import { fetchAllMeteringPoints } from "store/appSlice";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectMe, selectMeteringPoints } from "store/selectors";
import { MeteringPointTypes } from "helpers/enums";
import { ProtectedPaths } from "routes";
import FilterComponent from "components/FilterComponent/FilterComponent";
import {
  meteringPointAdminColumns,
  meteringPointAdminColumnsPlaceholders,
  meteringPointUserColumns,
  meteringPointUserColumnsPlaceholders,
} from "../meteringPointColumns";
import { getClientInfoFromRoles } from "../../../../../helpers/clientInfoFromRoles";
import downloadCSV from "../../../../../helpers/excelExport";
import { AttachFile } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { fetchExportMeteringPoints } from "../../../../../api/resources/meteringPoints";
import dayjs from "dayjs";
import MeteringPointActiveChip from "components/MeteringPointActiveChip";

interface RowType {
  row: {
    original: any; // your MeteringPoint type
  };
}

const MeteringPointsTable = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [sortParam, setSortParam] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );

  const dispatch = useAppDispatch();
  const meteringPoints = useAppSelector(selectMeteringPoints);
  const me = useAppSelector(selectMe);
  const isAdmin = me && getClientInfoFromRoles(me.roles).isAdmin;

  const columnsForFilter = isAdmin
    ? meteringPointAdminColumns
    : meteringPointUserColumns();

  const placeholdersForFilter = isAdmin
    ? meteringPointAdminColumnsPlaceholders
    : meteringPointUserColumnsPlaceholders();

  // URL search params from react-router
  const [searchParams, setSearchParams] = useSearchParams();

  // New: local filters state, synced with URL
  const [filters, setFilters] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const restoredFilters: { [key: string]: string } = {};
    searchParams.forEach((value, key) => {
      restoredFilters[key] = value;
    });

    if (me && !isAdmin) {
      const { clientName } = getClientInfoFromRoles(me.roles);
      if (clientName) {
        restoredFilters.client = clientName;
      }
    }

    setFilters(restoredFilters);

    const allFilters = sortParam
      ? { ...restoredFilters, sort: sortParam }
      : restoredFilters;

    dispatch(fetchAllMeteringPoints(currentPage, 25, allFilters));
  }, [dispatch, currentPage, sortParam, searchParams, me, isAdmin]);

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortParam(`${field},${direction}`);
  };

  const handleExportMeteringPoints = () => {
    setLoading(true);

    const filtersFromUrl: { [key: string]: string } = {};
    searchParams.forEach((value, key) => {
      filtersFromUrl[key] = value;
    });

    if (me && !isAdmin) {
      const { clientName } = getClientInfoFromRoles(me.roles);
      if (clientName) {
        filtersFromUrl.client = clientName;
      }
    }

    fetchExportMeteringPoints(filtersFromUrl)
      .then((response) => {
        setSnackbarMessage("Metering points exported successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        downloadCSV(response, `Metering-Points`, true);
      })
      .catch((error) => {
        setSnackbarMessage(error.message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        console.error("Error exporting contracts", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Called from FilterComponent when user changes filters
  const handleFilter = (newFilters: { [key: string]: string }) => {
    setFilters(newFilters);

    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });

    setSearchParams(params);
  };

  const getClientName = (client?: any) => {
    if (!client) return "";
    if (client.firstName) return `${client.firstName} ${client.lastName}`;
    return client.companyName;
  };

  const columns = [
    {
      accessorKey: "eic",
      header: "EIC",
    },
    {
      accessorKey: "client",
      header: "Client",
      cell: (row: RowType) => (
        <Link to={`/${ProtectedPaths.Clients}/${row.row.original.client.id}`}>
          {getClientName(row.row.original.client)}
        </Link>
      ),
    },
    {
      accessorKey: "address",
      header: "Address",
    },
    {
      accessorKey: "city",
      header: "City",
    },
    {
      accessorKey: "connectionState",
      header: "Status",
      cell: (row: RowType) =>
        MeteringPointTypes[
          row.row.original.connectionState ? "CONNECTED" : "DISCONNECTED"
        ],
    },
    {
      accessorKey: "consumptionScale",
      header: "Scale",
      cell: (row: RowType) =>
        MeteringPointTypes[
          row.row.original.consumptionScale as keyof typeof MeteringPointTypes
        ],
    },
    {
      accessorKey: "meteringType",
      header: "Type",
      cell: (row: RowType) =>
        MeteringPointTypes[
          row.row.original.meteringType ? "REMOTE_READING" : "ONE"
        ],
    },
    {
      accessorKey: "isBorderPoint",
      header: "Border",
      cell: (row: RowType) =>
        MeteringPointTypes[row.row.original.isBorderPoint ? "true" : "false"],
    },
    {
      accessorKey: "isActive",
      header: "Active",
      cell: (row: RowType) => (
        <MeteringPointActiveChip isActive={row.row.original.isActive} />
      ),
    },
    {
      accessorKey: "productionParks",
      header: "Production Park",
      cell: (row: RowType) => {
        const parks = row.row.original.productionParks ?? [];
        return (
          <>
            {parks.map((park: any) => (
              <Link
                to={`/${ProtectedPaths.EditProductionParks}/${park.id}`}
                key={`ProductionParkChip-${park.id}`}
              >
                <Chip label={park.name} size="small" clickable />
              </Link>
            ))}
          </>
        );
      },
    },
    {
      accessorKey: "operatorEic",
      header: "Operator",
    },
    {
      accessorKey: "lastReadingsSyncedAt",
      header: "Last sync",
      cell: (row: RowType) =>
        row.row.original.lastReadingsSyncedAt
          ? dayjs(row.row.original.lastReadingsSyncedAt).format(
              "DD.MM.YYYY HH:mm",
            )
          : "",
    },
  ];

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Metering points</Typography>
        <LoadingButton
          loading={loading}
          variant="contained"
          size="small"
          startIcon={<AttachFile />}
          onClick={handleExportMeteringPoints}
        >
          Export Metering Points
        </LoadingButton>
      </Box>

      <FilterComponent
        columns={columnsForFilter}
        columnPlaceholders={placeholdersForFilter}
        onFilter={handleFilter}
        filters={filters}
      />

      <Table
        data={meteringPoints?.content}
        isFetching={!meteringPoints}
        columns={columns}
        pageCount={meteringPoints?.totalPages}
        page={setCurrentPage}
        searchLabel="Search for metering point"
        onSort={handleSort}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MeteringPointsTable;
